import React, {useCallback, useEffect, useState} from "react";
import axios from 'axios';
import {Account, Authentication, IUserRoleEntity, RolesType} from 'pwm-shared';
import {API} from 'pwm-shared/src/types';
import AccessControl from 'pwm-shared/src/AccessControl';
import {useRouter} from "src/PWM/hooks/use-router";
import {useMounted} from "src/PWM/hooks/use-mounted";
import {useSearchParams} from "src/PWM/hooks/use-search-params";
import { paths } from "../paths";
import {apiSettings} from '../config';
import {PWMStorage} from '../utils/storage-util';
import {useToast} from '../hooks/use-toast';

const AuthenticationRoute: Authentication.Endpoint = 'authentication';
const TwelveHours = (new Date()).valueOf() + 1000 * 60 * 60 * 12;
const SESSION_ID = 'pwm_session_id';
export interface IAccount {
  name: string;
  username: string;
  firstName: string;
  lastName: string;
  mail: string;
  photo?: any;
  role: RolesType;
  userRoles: IUserRoleEntity[];
  oid: string;
  iat: number;
  exp: number;
}

export interface IAuthContext {
  account?: IAccount;
  expiresAt?: string;
  issuedAt?: string;
  jwt?: string;
  isSignedIn: boolean;
  isInitialized: boolean;
  logout?: () => Promise<void>;
  reAuthJwt?:() => Promise<void>;
  login?: (data: Authentication.CreateTokenParams) => Promise<void>;
  createToken?: (params: Authentication.CreateTokenParams) => Promise<any>;
  checkToken?: (token: string) => Promise<any>;
  isAdmin?: () => boolean;
}

export const AuthContext = React.createContext<IAuthContext>({isSignedIn: false, isInitialized: false});
AuthContext.displayName = 'AuthContext';
export const AuthProvider: React.FC<{ children: any; }> = (props) => {
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [jwt, setJwt] = React.useState<string | undefined>();
  const [userAccount, setUserAccount] = React.useState<IAccount | undefined>();
  const [expiresAt, setExpiresAt] = React.useState<string | undefined>();
  const [issuedAt, setIssuedAt] = React.useState<string | undefined>();
  const [checked, setChecked] = useState(false);
  
  const router = useRouter();
  const mounted = useMounted();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const toast = useToast();

  // Only check on mount, this allows us to redirect the user manually when auth state changes
  useEffect(
    () => {
      check();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getSignedJwt = async (user: IAccount) => {}

  useEffect(() => {
    if(isInitialized) {
      const iSL = Boolean(jwt);
      console.log(`Setting logged in state ${iSL}`);
      setIsSignedIn(iSL);
    }
  }, [jwt, isInitialized]);

  const createTokenFunction = async (params: Authentication.CreateTokenParams) : Promise<any> => {
    try {
      const {data: {data}} = await axios.post<API.Response<Authentication.CreateTokenResponse>>(`${apiSettings.baseUrl}/${AuthenticationRoute}`, params);
      if (data.authPath) {
        try {
          const url = new URL(data.authPath);
          return router.push(`${url.pathname}${url.search}`);
        } catch (e) {
          console.error(e);
        }
      }
    } catch (err) {
      console.error(err);
      console.log('handling potential server startup error');
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(createTokenFunction(params));
        }, 1000);
      });
    }
  };

  const value: IAuthContext = React.useMemo(() => {
    return {
      isSignedIn,
      account: userAccount,
      isInitialized,
      expiresAt,
      issuedAt,
      jwt,
      createToken: createTokenFunction,
      async checkToken(token: string) {
        const {data: {data}} = await axios.get<API.Response<Authentication.LoginResponse>>(`${apiSettings.baseUrl}/${AuthenticationRoute}`, {params: {token}});
        console.dir(data);
        if (data.success && data.account && data.token) {
          const {account, token, path} = data;
          PWMStorage.setItem(SESSION_ID, token);
          setJwt(token);
          setUserAccount(convertToAccount(account));
          if(path) {
            toast.success('You are logged in, redirecting to requested page.');
            router.replace(path);
          }
        } else {
          setJwt(undefined);
          setUserAccount(undefined);
        }
      },
      async login(params: Authentication.CreateTokenParams, redirect?: string) {
        // todo this is for when we go with username and password authentication
        const loginParams = {
          email: params.email,
          path: params.path
        };

        const {data: {data}} = await axios.post<API.Response<Authentication.LoginResponse>>(`${apiSettings.baseUrl}/${AuthenticationRoute}`, loginParams);
        console.dir(data);
        const {token, success, account} = data;
        if (success && account) {
          setJwt(token);
          setUserAccount(convertToAccount(account));
        }
      },
      async logout() {
        await axios.delete(`${apiSettings.baseUrl}/${AuthenticationRoute}`, {headers: {sessionid: jwt}});
        setUserAccount(undefined);
        setJwt(undefined);
        PWMStorage.clear();
        return;
      },
      async reAuthJwt() {
        if (userAccount) return getSignedJwt(userAccount)
      },
      isAdmin() {
        return AccessControl.isAdminRole(userAccount?.role)
      }
    };
  }, [isSignedIn, userAccount, isInitialized, expiresAt, issuedAt, jwt, setJwt, setUserAccount, toast, router]);

  const checkSessionActive = useCallback(async (sessionid: string) => {
    const {
      data: {
        data: {
          success,
          account
        }
      }
    } = await axios.get<API.Response<Authentication.CheckSessionActiveResponse>>(`${apiSettings.baseUrl}/${AuthenticationRoute}`, {
      params: {stillAlive: true},
      headers: {sessionid}
    });
    if (!success && value && value.logout) {
      await value.logout();
    }
    return {success, account};
  }, [value]);

  const checkSession = useCallback(async () => {
    if (window.location.pathname === paths.public.login.checkToken) {
      console.log('at check token screen, not checking for active session');
      return;
    }

    // check session key
    const sessionId = PWMStorage.getItem(SESSION_ID);
    if (sessionId) {
      console.log(`Found session ${sessionId}, checking if active`);
      const {success, account} = await checkSessionActive(sessionId);
      if (success && account) {
        setJwt(sessionId);
        setUserAccount({
          role: account.role,
          userRoles: account.userRoles,
          name: `${account.firstName} ${account.lastName}`,
          mail: account.emailAddress,
          username: account.emailAddress,
          firstName: account.firstName,
          lastName: account.lastName,
          oid: account.id + '',
          exp: TwelveHours,
          iat: (new Date()).valueOf()
        });
        toast.success('You are logged in');
      }
    }
  }, [checkSessionActive, setJwt, setUserAccount, toast]);

  const check = useCallback(async () => {
      if (!isInitialized) {
        await checkSession();
        setIsInitialized(true);
        return console.log('auth context not initialized, check returning');
      }
      if (mounted() && !isSignedIn && checked) {
        const searchParams = new URLSearchParams({returnTo: window.location.href}).toString();
        const href = paths.public.login.index + `?${searchParams}`;
        console.log(`redirecting to login screen ${href}`);
        router.replace(href);
      } else {
        console.log('checked and not signed in or mounted');
        setChecked(true);
      }
    },
    [mounted, isInitialized, isSignedIn, checked, router, checkSession, setIsInitialized]
  );

  return (
    <AuthContext.Provider value={value}>
      {props.children}
    </AuthContext.Provider>
  )
};

const convertToAccount = (account: Account.GetAccountResponse) => ({
  role: account.role,
  userRoles: account.userRoles,
  name: `${account.firstName} ${account.lastName}`,
  mail: account.emailAddress,
  username: account.emailAddress,
  firstName: account.firstName,
  lastName: account.lastName,
  oid: account.id + '',
  exp: TwelveHours,
  iat: (new Date()).valueOf()
})